import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useToasts, type Toast } from '@/lib/toast'

/**
 * Renders the stack of live toasts. Anchored to top of viewport under the safe-area.
 * Each toast is a glass tile with an optional coral/mint bloom — clickable if the
 * toast has a url, else just informative. Auto-dismisses after ttl.
 */
export function ToastContainer() {
  const toasts = useToasts((s) => s.toasts)
  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-50 flex flex-col items-center gap-2 px-3"
      style={{ top: 'calc(env(safe-area-inset-top) + 12px)' }}
    >
      <AnimatePresence>
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} />
        ))}
      </AnimatePresence>
    </div>
  )
}

function ToastItem({ toast }: { toast: Toast }) {
  const nav = useNavigate()
  const dismiss = useToasts((s) => s.dismiss)
  const bloomClass = toast.tone === 'coral' ? 'bloom-coral' : toast.tone === 'mint' ? 'bloom-mint' : ''

  const handleClick = () => {
    if (toast.url) nav(toast.url)
    dismiss(toast.id)
  }

  return (
    <motion.button
      type="button"
      onClick={handleClick}
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -12, scale: 0.9 }}
      transition={{ duration: 0.28, ease: [0.34, 1.56, 0.64, 1] }}
      className={`pointer-events-auto glass-tile relative overflow-hidden text-left w-full max-w-sm ${bloomClass}`}
      style={{ padding: '14px 18px' }}
    >
      <div className="relative z-10 flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-caption font-semibold text-text-primary">{toast.title}</div>
          <div className="text-caption text-text-secondary truncate">{toast.body}</div>
        </div>
        <span
          className="text-caption text-text-quiet shrink-0"
          onClick={(e) => { e.stopPropagation(); dismiss(toast.id) }}
        >
          ✕
        </span>
      </div>
    </motion.button>
  )
}
